/**
 * Cupons Page Manager
 * Gerencia modal de newsletter e liberação de cupons
 */

class CuponsPageManager {
  constructor() {
    this.modal = document.querySelector('[data-newsletter-modal]');
    this.modalOverlay = document.querySelector('[data-modal-overlay]');
    this.closeButtons = document.querySelectorAll('[data-modal-close]');
    this.form = document.querySelector('[data-newsletter-form]');
    this.openButtons = document.querySelectorAll('[data-open-newsletter-modal]');
    this.currentCupomCard = null;
    
    // LocalStorage keys
    this.storageKey = 'cupons_newsletter_subscribed';
    this.pendingCupomKey = 'cupons_pending_reveal';
    
    this.init();
  }

  init() {
    if (!this.modal || !this.form) return;
    
    this.bindEvents();
    this.checkPendingCupom();
    this.checkSubscriptionStatus();
  }

  bindEvents() {
    // Open modal buttons
    this.openButtons.forEach(button => {
      button.addEventListener('click', (e) => this.handleOpenModal(e));
      button.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.handleOpenModal(e);
        }
      });
    });

    // Close modal buttons
    this.closeButtons.forEach(button => {
      button.addEventListener('click', () => this.handleCloseModal());
      button.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.handleCloseModal();
        }
      });
    });

    // Overlay click
    this.modalOverlay.addEventListener('click', () => this.handleCloseModal());

    // ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.modal.hasAttribute('aria-hidden')) {
        this.handleCloseModal();
      }
    });
    
    // Check if form was submitted successfully
    this.checkFormSuccess();
  }

  handleOpenModal(event) {
    const button = event.currentTarget;
    this.currentCupomCard = button.closest('[data-cupom-id]');
    
    // Check if user already subscribed
    if (this.isSubscribed()) {
      this.revealCupom(this.currentCupomCard);
      return;
    }

    // Save cupom ID to localStorage for after form submission
    if (this.currentCupomCard) {
      const cupomId = this.currentCupomCard.dataset.cupomId;
      localStorage.setItem(this.pendingCupomKey, cupomId);
    }

    this.openModal();
  }

  openModal() {
    this.modal.removeAttribute('aria-hidden');
    document.body.style.overflow = 'hidden';
    
    // Focus on first input
    setTimeout(() => {
      const firstInput = this.form.querySelector('input');
      if (firstInput) firstInput.focus();
    }, 100);
  }

  handleCloseModal() {
    this.modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    this.currentCupomCard = null;
    
    // Clear form
    this.form.reset();
    this.clearErrors();
    this.hideSuccess();
  }

  checkFormSuccess() {
    // Check if form was successfully submitted (Shopify reloads page with success message)
    const successMessage = this.form.querySelector('[data-success-message]');
    
    if (successMessage && successMessage.offsetParent !== null) {
      // Form was submitted successfully
      this.markAsSubscribed();
      
      // Hide success message after 2 seconds and reveal pending cupom
      setTimeout(() => {
        successMessage.style.display = 'none';
      }, 2000);
    }
  }

  checkPendingCupom() {
    // Check if there's a pending cupom to reveal after successful form submission
    const pendingCupomId = localStorage.getItem(this.pendingCupomKey);
    const successMessage = this.form.querySelector('[data-success-message]');
    
    if (pendingCupomId && successMessage && successMessage.offsetParent !== null) {
      // Find the cupom card by ID
      const cupomCard = document.querySelector(`[data-cupom-id="${pendingCupomId}"]`);
      
      if (cupomCard) {
        // Clear the pending cupom
        localStorage.removeItem(this.pendingCupomKey);
        
        // Reveal the cupom after showing success message
        setTimeout(() => {
          this.revealCupom(cupomCard);
        }, 2000);
      }
    }
  }

  revealCupom(cupomCard) {
    if (!cupomCard) return;
    
    const cupomCode = cupomCard.dataset.cupomCode;
    const actionsDiv = cupomCard.querySelector('.cupom-card__actions');
    
    if (!actionsDiv) return;
    
    // Replace with cupom code reveal
    actionsDiv.innerHTML = `
      <div class="cupom-card__revealed">
        <p class="cupom-card__code-label">Seu código:</p>
        <div class="cupom-card__code-container">
          <code class="cupom-card__code" data-cupom-code-text>${cupomCode}</code>
          <button 
            type="button" 
            class="cupom-card__copy-button"
            data-copy-button
            aria-label="Copiar código ${cupomCode}"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" stroke-width="2"/>
              <path d="M5 15H4C2.89543 15 2 14.1046 2 13V4C2 2.89543 2.89543 2 4 2H13C14.1046 2 15 2.89543 15 4V5" stroke="currentColor" stroke-width="2"/>
            </svg>
            <span>Copiar</span>
          </button>
        </div>
      </div>
    `;
    
    // Add copy functionality
    const copyButton = actionsDiv.querySelector('[data-copy-button]');
    if (copyButton) {
      copyButton.addEventListener('click', () => this.copyCupomCode(cupomCode, copyButton));
    }
    
    // Scroll to cupom
    cupomCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
    
    // Add highlight animation
    cupomCard.classList.add('cupom-card--revealed');
  }

  copyCupomCode(code, button) {
    navigator.clipboard.writeText(code).then(() => {
      const originalHTML = button.innerHTML;
      button.innerHTML = `
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span>Copiado!</span>
      `;
      button.classList.add('cupom-card__copy-button--success');
      
      setTimeout(() => {
        button.innerHTML = originalHTML;
        button.classList.remove('cupom-card__copy-button--success');
      }, 2000);
    }).catch(err => {
      console.error('Erro ao copiar:', err);
    });
  }

  clearErrors() {
    const errorElements = this.form.querySelectorAll('[data-error-name], [data-error-email]');
    const inputElements = this.form.querySelectorAll('input');
    
    errorElements.forEach(el => {
      el.textContent = '';
      el.style.display = 'none';
    });
    
    inputElements.forEach(input => {
      input.classList.remove('newsletter-modal__input--error');
      input.removeAttribute('aria-invalid');
    });
  }

  showSuccess() {
    const successElement = this.form.querySelector('[data-success-message]');
    if (successElement) {
      successElement.style.display = 'block';
    }
  }

  hideSuccess() {
    const successElement = this.form.querySelector('[data-success-message]');
    if (successElement) {
      successElement.style.display = 'none';
    }
  }

  isSubscribed() {
    return localStorage.getItem(this.storageKey) === 'true';
  }

  markAsSubscribed() {
    localStorage.setItem(this.storageKey, 'true');
  }

  checkSubscriptionStatus() {
    if (this.isSubscribed()) {
      // Reveal all cupoms for subscribed users
      document.querySelectorAll('[data-cupom-id]').forEach(card => {
        // Revelar o cupom diretamente em vez de apenas mudar o texto do botão
        this.revealCupom(card);
      });
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CuponsPageManager();
  });
} else {
  new CuponsPageManager();
}

